import type { ComponentProps } from 'react';
import { cn } from '@/lib/cn';

/**
 * Tabela com rolagem horizontal **contida no próprio contêiner**.
 * O `body` nunca rola na horizontal (PLANO_GERAL.md §4.4).
 */
export function TableWrapper({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('w-full overflow-x-auto', className)} {...props} />;
}

export function Table({ className, ...props }: ComponentProps<'table'>) {
  return <table className={cn('w-full caption-bottom text-sm', className)} {...props} />;
}

/**
 * Cabeçalho da tabela, opcionalmente **preso no topo** ao rolar.
 *
 * O `sticky` vai nos `th`, e não no `thead`: em tabela, o navegador só gruda a célula.
 * Precisa de fundo próprio — sem ele as linhas passariam por baixo e se misturariam ao
 * texto do cabeçalho, que é pior do que não grudar.
 *
 * O fundo é `surface-raised`, e não `surface`: toda tabela deste sistema vive dentro de
 * um `Card`, que é quem pinta `surface-raised`. Com o tom da página, o cabeçalho preso
 * viraria uma faixa de cor diferente correndo por cima do card — no tema escuro, cinco
 * pontos de luminosidade de diferença.
 *
 * A borda de baixo acompanha os `th` pelo mesmo motivo: a do `thead` fica na posição
 * original e rola embora, deixando o cabeçalho preso encostado nas células.
 *
 * Só faz sentido dentro de um `TableWrapper` que rola na vertical; numa tabela curta ele
 * não tem o que fazer, e por isso é opção e não padrão.
 */
export function TableHeader({
  className,
  sticky = false,
  ...props
}: ComponentProps<'thead'> & { sticky?: boolean }) {
  return (
    <thead
      className={cn(
        'border-b border-(--color-border-subtle)',
        sticky &&
          '[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:border-b [&_th]:border-(--color-border-subtle) [&_th]:bg-(--color-surface-raised)',
        className,
      )}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'border-b border-(--color-border-subtle) transition-colors hover:bg-(--color-surface-muted)',
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'h-10 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wide text-(--color-content-muted)',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<'td'>) {
  return <td className={cn('px-3 py-2.5 align-middle', className)} {...props} />;
}
